const ensureMeta = (query, attribute, value) => {
  let element = document.querySelector(query);
  const existed = Boolean(element);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }

  return { element, existed };
};

const ensureLink = (query, relValue) => {
  let element = document.querySelector(query);
  const existed = Boolean(element);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', relValue);
    document.head.appendChild(element);
  }

  return { element, existed };
};

export const applyPageMetadata = ({
  title,
  description,
  keywords,
  canonicalPath,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  twitterTitle,
  twitterDescription,
  robots,
}) => {
  const previous = {
    title: document.title,
  };

  if (title) {
    document.title = title;
  }

  const descriptionTag = ensureMeta('meta[name="description"]', 'name', 'description');
  const keywordsTag = ensureMeta('meta[name="keywords"]', 'name', 'keywords');
  const robotsTag = ensureMeta('meta[name="robots"]', 'name', 'robots');
  const ogTitleTag = ensureMeta('meta[property="og:title"]', 'property', 'og:title');
  const ogDescriptionTag = ensureMeta('meta[property="og:description"]', 'property', 'og:description');
  const ogTypeTag = ensureMeta('meta[property="og:type"]', 'property', 'og:type');
  const ogUrlTag = ensureMeta('meta[property="og:url"]', 'property', 'og:url');
  const twitterTitleTag = ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title');
  const twitterDescriptionTag = ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description');
  const canonicalTag = ensureLink('link[rel="canonical"]', 'canonical');

  const originalValues = {
    description: descriptionTag.element.getAttribute('content'),
    keywords: keywordsTag.element.getAttribute('content'),
    robots: robotsTag.element.getAttribute('content'),
    ogTitle: ogTitleTag.element.getAttribute('content'),
    ogDescription: ogDescriptionTag.element.getAttribute('content'),
    ogType: ogTypeTag.element.getAttribute('content'),
    ogUrl: ogUrlTag.element.getAttribute('content'),
    twitterTitle: twitterTitleTag.element.getAttribute('content'),
    twitterDescription: twitterDescriptionTag.element.getAttribute('content'),
    canonicalHref: canonicalTag.element.getAttribute('href'),
  };

  if (description) descriptionTag.element.setAttribute('content', description);
  if (keywords) keywordsTag.element.setAttribute('content', keywords);
  if (robots) robotsTag.element.setAttribute('content', robots);
  if (ogTitle || title) ogTitleTag.element.setAttribute('content', ogTitle || title);
  if (ogDescription || description) ogDescriptionTag.element.setAttribute('content', ogDescription || description);
  ogTypeTag.element.setAttribute('content', ogType);

  const resolvedCanonical = canonicalUrl || (canonicalPath ? `${window.location.origin}${canonicalPath}` : null);
  const resolvedUrl = ogUrl || resolvedCanonical || window.location.href;
  ogUrlTag.element.setAttribute('content', resolvedUrl);

  if (twitterTitle || title) twitterTitleTag.element.setAttribute('content', twitterTitle || title);
  if (twitterDescription || description) twitterDescriptionTag.element.setAttribute('content', twitterDescription || description);

  if (resolvedCanonical) {
    canonicalTag.element.setAttribute('href', resolvedCanonical);
  }

  return () => {
    document.title = previous.title;

    const restoreOrRemove = (tagRef, value) => {
      if (!tagRef.existed && (value === null || value === undefined)) {
        tagRef.element.remove();
        return;
      }

      if (value === null || value === undefined) {
        tagRef.element.removeAttribute('content');
      } else {
        tagRef.element.setAttribute('content', value);
      }
    };

    restoreOrRemove(descriptionTag, originalValues.description);
    restoreOrRemove(keywordsTag, originalValues.keywords);
    restoreOrRemove(robotsTag, originalValues.robots);
    restoreOrRemove(ogTitleTag, originalValues.ogTitle);
    restoreOrRemove(ogDescriptionTag, originalValues.ogDescription);
    restoreOrRemove(ogTypeTag, originalValues.ogType);
    restoreOrRemove(ogUrlTag, originalValues.ogUrl);
    restoreOrRemove(twitterTitleTag, originalValues.twitterTitle);
    restoreOrRemove(twitterDescriptionTag, originalValues.twitterDescription);

    if (!canonicalTag.existed && (originalValues.canonicalHref === null || originalValues.canonicalHref === undefined)) {
      canonicalTag.element.remove();
    } else if (originalValues.canonicalHref === null || originalValues.canonicalHref === undefined) {
      canonicalTag.element.removeAttribute('href');
    } else {
      canonicalTag.element.setAttribute('href', originalValues.canonicalHref);
    }
  };
};

export const applyStructuredData = (schemas = []) => {
  const createdScripts = [];

  schemas.forEach((schema, index) => {
    if (!schema || typeof schema !== 'object') return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema-index', String(index));
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    createdScripts.push(script);
  });

  return () => {
    createdScripts.forEach((script) => script.remove());
  };
};
